import React, { useState } from 'react';
import useIntls from '@opm/hooks/useIntls';
import SortBySelect from '@op/components/SelectComponents/SortBySelect';
import SingleTranslateSelect from '@op/components/FilterBoxCommon/filters/languages/singleLauage';
import SvgIcon from '@home/components/SvgIcon';
import VideoCard from '@opm/pages/Videos/components/videoCard';
import { LanguageType } from '@op/types/common';
import VideoCover from '../../CardView/VideoCover';
import { ICreativeItem } from '../../types';
import { useDrawerRelatedCreatives } from '../../hooks/useDrawerRelatedCreatives';
import CardCommon from '@op/components/CardCommon';
import { ADSortOptions } from '@opm/pages/CompetitorsAdCreatives/constants';
import { VIDEO_SORT_OPTIONS } from '@opm/utils/videoAdapter';

interface IRelatedCreativesProps {
  /** 上层 filter（含 game_id, topic, start_time, end_time, channels 等） */
  filter?: any;
  onViewAll?: () => void;
  onThumbnailClick?: (item: ICreativeItem) => void;
  title?: string;
}

/**
 * Related Creatives 区（Figma 3902_62017 Section 3）
 *
 * - 标题 `Related Creatives (n)` + 右侧 `View All →`
 * - SortBy 下拉（独立，仅作用于 Drawer 内）
 * - 2 列网格，单次拉 page=1, page_size=8
 * - 视频卡片复用 VideoCover
 *
 * 接口：POST /api/v1/marketing/creative_trends/related_creatives
 */
export default function RelatedCreatives({
  filter,
  onViewAll,
  onThumbnailClick,
  title = 'Related Creatives',
}: IRelatedCreativesProps) {
  const { intl } = useIntls();
  const isUgcSource = filter?.source_type === 'ugc';

  const [sortBy, setSortBy] = useState<any>({
    by: isUgcSource ? 'tweets_view' : 'impression_number',
    order: 'desc',
  });
  // 视频翻译切换（origin / en / zh）—— 仅 UGC 分支生效，对应 related_videos API 的
  // title / title_en / title_cn 三态字段；CT 分支用 VideoCover，不涉及 title 翻译
  const [language, setLanguage] = useState<LanguageType>('origin');

  const { list, loading } = useDrawerRelatedCreatives({
    filter,
    sortItem: sortBy?.by,
    sortModel: sortBy?.order,
  });

  return (
    <CardCommon
      className="dbom-ct-drawer-creatives"
      loading={loading}
      empty={list.length === 0}
      emptyHeight={190}
      loadingHeight={190}
      contentCard
      title={(
        <span className="dbom-ct-drawer-creatives__title">
          {intl(title)}
          {/* <span className="dbom-ct-drawer-creatives__count">
            {`(${total})`}
          </span> */}
        </span>
      )}
      dropdownsFilterBeforeElement={(
        <>
          {onViewAll && (
            <a
              className="dbom-ct-drawer-creatives__view-all"
              onClick={(e) => {
                e.stopPropagation();
                onViewAll();
              }}
            >
              <span>{intl('View All')}</span>
              <SvgIcon type="arrow-right" className="card-slot-icon" />
            </a>
          )}
        </>
      )}
    >
      <div className="dbom-ct-drawer-creatives__sort">
        <SortBySelect
          options={isUgcSource ? VIDEO_SORT_OPTIONS : ADSortOptions}
          defaultSortBy={sortBy}
          changeSortBy={(v: any) => setSortBy(v)}
        />
        {/* {isUgcSource && (
          <SingleTranslateSelect
            defaultValue={language}
            onChange={(_key: any, value: LanguageType) => setLanguage(value)}
          />
        )} */}
      </div>
      <div className="dbom-ct-drawer-creatives__grid">
        {list.map((item: any, idx: number) =>
          // UGC 分支：item 已经在 useDrawerRelatedCreatives 层适配成 VideoCard 形状
          isUgcSource ? (
            <VideoCard
              key={item.video_url || idx}
              item={item}
              language={language}
              hideKeys={['comments']}
            />
          ) : (
            <VideoCover
              key={item.video_url || idx}
              data={item as ICreativeItem}
              filter={filter}
              onPlay={(playItem) => onThumbnailClick?.(playItem as any)}
            />
          ),
        )}
      </div>
    </CardCommon>
  );
}
